import React, { useEffect, useReducer, useRef } from 'react';
import { withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import styles from './styles';
import LocationCard from '../LocationCard';
import appConstants from '../../../../../../appConstants';
import Loader from '../components/Loader';
import Events from '../../../../../../utilities/EventEmitter';
import drawConstant from '../../../../Settings/common/DrawTools/DrawConstant';
import { API_STATUS_MSG } from '../../constants';
import AlertPopup from '../../../../Settings/Terminal/RouteMappingTool/components/common/AlertPopup';
import { getLoaderMessage, isSameLocation, checkIfOverlappingRegions, isLocationMappingEditable } from '../../utils';
import { withUserProfile } from '../../../../../../userProfile/userProfileContext';
import { initialState, locationMappingReducer, ACTION_TYPES } from './reducer';

const BaseLocationMapping = ({
  classes,
  equipmentSizeList,
  enqueueSnackbar,
  locationMappingData,
  getLocationMappingData,
  isDataLoaded,
  onDeleteLocation,
  terminalId,
  isLocationDeleteInProgress,
  geoDataList,
  glEventHub,
  mapId,
  userData: { userProfile },
  saveLocationMapping,
  locationLabel = 'Location',
  EditForm,
  DisplayCard,
  isCustomerLocationFlow = false,
}) => {
  let timerRef = useRef(null);
  let selectPolygonTimer = useRef(null);
  let locationCardRef = useRef(null);
  let newLocationCardRef = useRef(null);

  const [state, dispatch] = useReducer(locationMappingReducer, initialState);
  const {
    localLocationMappingData,
    newLocationMappingInfo,
    isLocationUpdateInProgress,
    isOperationDelayed,
    drawingInProgress,
    isNewLocation,
    selectedLocationCardId,
    selectedFeatureIdsOnMap,
    isOverlappingRegions,
  } = state;

  const isUpdateInProgress =
    isLocationUpdateInProgress || !isDataLoaded || isLocationDeleteInProgress;

  useEffect(() => {
    const { ROUTE_MAPPING_EVENTS } = drawConstant;
    Events.on(`${mapId}_${ROUTE_MAPPING_EVENTS.DRAWING_UPDATED}`, handleDrawingUpdate);
    Events.on(`${mapId}_${ROUTE_MAPPING_EVENTS.DRAWING_DELETED}`, handleDrawingDelete);
    Events.on(`${mapId}_${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_IN_PROGRESS}`, handleInProgressDrawing);
    Events.on(`${mapId}_${ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP}`, handleDrawingSelectedOnMap);
    Events.on(`${mapId}_${ROUTE_MAPPING_EVENTS.CLOSE_FORM}`, closeForm);
    return () => {
      Events.off(`${mapId}_${ROUTE_MAPPING_EVENTS.DRAWING_UPDATED}`, handleDrawingUpdate);
      Events.off(`${mapId}_${ROUTE_MAPPING_EVENTS.DRAWING_DELETED}`, handleDrawingDelete);
      Events.off(`${mapId}_${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_IN_PROGRESS}`, handleInProgressDrawing);
      Events.off(`${mapId}_${ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP}`, handleDrawingSelectedOnMap);
      Events.off(`${mapId}_${ROUTE_MAPPING_EVENTS.CLOSE_FORM}`, closeForm);
      clearTimeout(timerRef);
      clearTimeout(selectPolygonTimer);
    };
  }, [localLocationMappingData, geoDataList]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPES.SET_STATE_FROM_PROPS, payload: locationMappingData });
  }, [locationMappingData]);

  const getDefaultEditInfo = (feature) => {
    return {
      locationMappingId: feature ? feature.id : '',
      locationName: '',
      maxEquipmentSize: '',
      minEquipmentSize: '',
      isLiftGateRequired: false,
      boundary: feature ? feature.geometry.coordinates[0] : [],
      isNewLocationMapping: true
    };
  };

  const handleDrawingUpdate = (feature) => {
    checkForOverLappingRegion(feature);
    const isExistingFeature = isAlreadyExistingFeature(feature.id);
    const boundary = feature?.geometry?.coordinates[0];
    if (isExistingFeature) {
      updateCustomersGeoBoundaryById(feature.id, boundary);
      dispatch({ type: ACTION_TYPES.SET_SELECTED_LOCATION_CARD_ID, payload: feature.id });
    } else {
      const newLocationMappingInfoPayload = {
        ...(newLocationMappingInfo ? newLocationMappingInfo : getDefaultEditInfo(feature)),
        boundary
      };
      dispatch({
        type: ACTION_TYPES.HANDLE_DRAWING_UPDATE,
        payload: {
          newLocationMappingInfo: newLocationMappingInfoPayload,
          isNewLocation: true,
        },
      });
      scrollToNewLocationCard();
    }
  };

  const checkForOverLappingRegion = (newBoundary) => {
    const isNewOverlappingRegions = checkIfOverlappingRegions(geoDataList, newBoundary);
    if (isNewOverlappingRegions) {
      enqueueSnackbar('Overlapping regions are not allowed.', {
        variant: 'error'
      });
    }
    dispatch({ type: ACTION_TYPES.SET_IS_OVERLAPPING_REGIONS, payload: isNewOverlappingRegions });
  };

  const updateCustomersGeoBoundaryById = (locationMappingId, boundary) => {
    dispatch({ type: ACTION_TYPES.UPDATE_CUSTOMERS_GEO_BOUNDARY, payload: { locationMappingId, boundary } });
  };

  const handleInProgressDrawing = () => {
    dispatch({ type: ACTION_TYPES.SET_DRAWING_IN_PROGRESS, payload: true });
  };

  const handleDrawingSelectedOnMap = (featureIds) => {
    dispatch({ type: ACTION_TYPES.SET_SELECTED_FEATURE_IDS_ON_MAP, payload: featureIds });
    scrollToSelectedDrawingCard(featureIds[0]);
  };

  const scrollToNewLocationCard = () => {
    newLocationCardRef?.current?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth'
    })
  }

  const scrollToSelectedDrawingCard = (id) => {
    let matchedRef;
    locationCardRef?.current?.childNodes?.forEach((node) => {
      if (Number(node.id) === Number(id)) {
        matchedRef = node;
      }
    });
    matchedRef?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth'
    })
  };

  const getExistingFeature = (featureId) => {
    return localLocationMappingData.find((item) => item.locationMappingId === featureId) ?? {};
  };

  const handleDrawingDelete = (feature) => {
    const matchedItem = getExistingFeature(feature.id);
    if (matchedItem) {
      onDeleteLocation(matchedItem);
      return;
    }
    closeForm({});
  };

  const isAlreadyExistingFeature = (id) => {
    return Boolean(getExistingFeature(id)?.locationMappingId);
  };

  const onLocationProgressTimeOut = () => {
    clearTimeout(timerRef);
    timerRef = setTimeout(() => {
      dispatch({ type: ACTION_TYPES.OPERATION_DELAYED });
    }, appConstants.ONE_MINUTE_TIME_OUT);
  };

  const handleSubmitLocation = (formData) => {
    if (isOverlappingRegions) {
      enqueueSnackbar('Overlapping regions are not allowed.', {
        variant: 'error'
      });
      return;
    }
    const locationMappingId = formData.locationMappingId;
    dispatch({ type: ACTION_TYPES.SUBMIT_LOCATION_START });
    onLocationProgressTimeOut();
    
    saveLocationMapping({terminalId, ...formData}).then(
      (response) => {
        if (response?.status >= 200 && response?.status < 400) {
          const isExistingFeature = Boolean(locationMappingId);
          Events.emit(
            `${mapId}_${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_CREATED_ON_MAP}`,
            'save',
            isExistingFeature,
            locationMappingId
          );
          dispatch({ type: ACTION_TYPES.SUBMIT_LOCATION_FINISH });
          closeForm({
            isDataSaved: true,
            locationMappingId
          });
          enqueueSnackbar(API_STATUS_MSG.UPDATE_SUCCESS, {
            variant: 'success'
          });
          dispatch({ type: ACTION_TYPES.SET_SELECTED_LOCATION_CARD_ID, payload: null });
          getLocationMappingData();
          clearTimeout(timerRef);
          isCustomerLocationFlow && glEventHub.emit('RefreshCustomerData');
          return;
        }
        handleError(response?.status);
      },
      () => {
        handleError();
      }
    );
  };

  const handleError = (statusCode) => {
    dispatch({ type: ACTION_TYPES.SUBMIT_LOCATION_FINISH });
    clearTimeout(timerRef);
    if (statusCode === 403) {
      return;
    }
    enqueueSnackbar(API_STATUS_MSG.UPDATE_FAILED, {
      variant: 'error'
    });
  };

  const closeForm = ({ isDataSaved = false, locationMappingId }) => {
    const locationId = locationMappingId || newLocationMappingInfo?.locationMappingId;
    const { isNewLocationMapping = false } = newLocationMappingInfo ?? {};
    const isExistingFeature = isAlreadyExistingFeature(locationId);
    let newSelectedFeatureIdsOnMap;
    if (!isDataSaved) {
      Events.emit(
        `${mapId}_${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_CREATED_ON_MAP}`,
        'cancel',
        isExistingFeature,
        locationId
      );
      newSelectedFeatureIdsOnMap = [];
    } else {
      newSelectedFeatureIdsOnMap = selectedFeatureIdsOnMap;
    }
    dispatch({
      type: ACTION_TYPES.CLOSE_FORM,
      payload: { isNewLocationMapping, locationId, newSelectedFeatureIdsOnMap },
    });
    selectPolygonTimer && clearTimeout(selectPolygonTimer);
  };

  const handleUpdateInProgressPopupClose = () => {
    dispatch({ type: ACTION_TYPES.SUBMIT_LOCATION_FINISH });
  };

  const selectPolygonOnMap = (selectedPolygonIds, isEditMode) => {
    Events.emit(
      `${mapId}_${drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_SIDE_PANEL}`,
      selectedPolygonIds,
      isEditMode
    );
  };

  const openLocationMappingForm = (id) => {
    selectPolygonTimer = setTimeout(() => {
      selectPolygonOnMap([id], true);
    });
    dispatch({ type: ACTION_TYPES.OPEN_LOCATION_MAPPING_FORM, payload: id });
    closeForm({isDataSaved: false, locationMappingId: newLocationMappingInfo?.locationMappingId})
  };

  const isLocationNameAlreadyExist = ({locationName, isNewLocation}) => {
    if (!locationName) {
      return false;
    }
    const matchedLocationNameCount = localLocationMappingData.filter((location) => {
      return isSameLocation(location.locationName, locationName);
    }).length;
    return isNewLocation ? matchedLocationNameCount >= 1 : matchedLocationNameCount > 1;
  };

  return (
    <div
      className={`${classes.contentContainer} customScroll ${
        isUpdateInProgress ? classes.loading : ''
      }`}>
      {isNewLocation && !selectedLocationCardId && (
        <div ref={newLocationCardRef}>
          <div className={classes.locationLabel}>New {locationLabel}</div>
          <EditForm
            isSelectedOnMap={selectedFeatureIdsOnMap?.includes(
              newLocationMappingInfo?.locationMappingId
            )}
            location={newLocationMappingInfo ?? {}}
            onLocationUpdate={handleSubmitLocation}
            onCancelEdit={closeForm}
            equipmentSizeList={equipmentSizeList}
            isNewLocation={isNewLocation}
            selectPolygonOnMap={selectPolygonOnMap}
            isLocationNameAlreadyExist={isLocationNameAlreadyExist}
          />
        </div>
      )}
      <div className={classes.prevLocationContainer}>
        {localLocationMappingData.length > 0 ? (
          <>
            <div className={`${classes.locationLabel} ${classes.paddingBottom10}`}>
              Existing {locationLabel}s
            </div>
            <div ref={locationCardRef}>
              {!isUpdateInProgress &&
                localLocationMappingData.map((location) => (
                  <LocationCard
                    isSelectedOnMap={selectedFeatureIdsOnMap?.includes(
                      location.locationMappingId
                    )}
                    location={location}
                    EditForm={EditForm}
                    DisplayCard={DisplayCard}
                    onEditLocation={handleSubmitLocation}
                    onDeleteLocation={onDeleteLocation}
                    equipmentSizeList={equipmentSizeList}
                    openForm={openLocationMappingForm}
                    onClose={closeForm}
                    selectPolygonOnMap={selectPolygonOnMap}
                    key={location.locationMappingId}
                    isLocationNameAlreadyExist={isLocationNameAlreadyExist}
                    isEditable={
                      isLocationMappingEditable(userProfile) &&
                      !isNewLocation &&
                      selectedLocationCardId === location.locationMappingId
                    }
                  />
                ))}
            </div>
          </>
        ) : (
          <div className={classes.dataNotFound}>Mapped {locationLabel} Not Available</div>
        )}
      </div>
      {isUpdateInProgress ? (
        <Loader
          close={handleUpdateInProgressPopupClose}
          delayed={isOperationDelayed}
          message={getLoaderMessage(
            isLocationUpdateInProgress || isLocationDeleteInProgress,
            isOperationDelayed
          )}
        />
      ) : null}
      {drawingInProgress && (
        <AlertPopup
          title="Drawing in progress"
          message="Drawing in progress, please save or cancel to continue."
          onClose={() => dispatch({ type: ACTION_TYPES.SET_DRAWING_IN_PROGRESS, payload: false })}
        />
      )}
    </div>
  );
};

export default compose(withStyles(styles), withSnackbar, withUserProfile)(BaseLocationMapping); 